import { SimpleProjectionYearResult, SharePriceCAGRResults } from '../../../../types/SimpleProjection';

/**
 * Calculates Compound Annual Growth Rate (CAGR) between two values over a period.
 * Formula: CAGR = (EndValue / StartValue)^(1/years) - 1
 *
 * @param startValue - The initial value
 * @param endValue - The final value
 * @param years - Number of years between start and end
 * @returns CAGR as a decimal (e.g., 0.10 for 10%)
 */
export const calculateCAGR = (
  startValue: number | null,
  endValue: number | null,
  years: number
): number | null => {
  // Null inputs propagate as null — share-price CAGR isn't defined when
  // either endpoint is missing (e.g. EPS was negative for that year).
  if (startValue === null || endValue === null) return null;
  if (startValue <= 0 || years <= 0) return null;
  if (endValue <= 0) return -1; // -100% if end value is zero or negative

  return Math.pow(endValue / startValue, 1 / years) - 1;
};

/**
 * Calculates CAGR for share price low and high from projection results.
 * Compares year 0 (historical) to the final projected year.
 *
 * @param projections - Array of projection results including historical year 0
 * @returns Object containing CAGR for sharePriceLow and sharePriceHigh
 */
export const calculateSharePriceCAGRs = (projections: SimpleProjectionYearResult[]): SharePriceCAGRResults | null => {
  if (!projections || projections.length < 2) return null;

  const historicalYear = projections[0];
  const finalYear = projections[projections.length - 1];
  const years = projections.length - 1; // Number of projection years (excluding historical)

  const lowCagr = calculateCAGR(historicalYear.sharePriceLow, finalYear.sharePriceLow, years);
  const highCagr = calculateCAGR(historicalYear.sharePriceHigh, finalYear.sharePriceHigh, years);

  if (lowCagr === null && highCagr === null) return null;

  return {
    sharePriceLow: lowCagr,
    sharePriceHigh: highCagr,
  };
};

/**
 * Calculates CAGR from year 0 to a specific target year.
 * Used for displaying cumulative CAGR at each year in the table.
 *
 * @param projections - Array of projection results
 * @param targetYearIndex - Index of the year to calculate CAGR to (1-based, e.g., 2 for year 2)
 * @returns Object containing CAGR for sharePriceLow and sharePriceHigh to that year
 */
export const calculateCAGRToYear = (
  projections: SimpleProjectionYearResult[],
  targetYearIndex: number
): SharePriceCAGRResults | null => {
  if (!projections || projections.length < 2 || targetYearIndex < 1 || targetYearIndex >= projections.length) {
    return null;
  }

  const historicalYear = projections[0];
  const targetYear = projections[targetYearIndex];
  const years = targetYearIndex; // Number of years from historical to target

  return {
    sharePriceLow: calculateCAGR(historicalYear.sharePriceLow, targetYear.sharePriceLow, years),
    sharePriceHigh: calculateCAGR(historicalYear.sharePriceHigh, targetYear.sharePriceHigh, years),
  };
};

